import {
  getBlockedFileErrorMessage,
  isBlockedExecutable,
} from '../../constants/security';

/**
 * Raw bytes that still fit in `express.json({ limit: '150mb' })` after base64
 * expansion. A 150MB file becomes about 200MB of JSON and never reaches save.
 */
export const USER_FEEDBACK_MAX_FILE_BYTES = 100 * 1024 * 1024;
export const FILE_TOO_LARGE_MESSAGE = 'File size exceeds 100MB limit.';

export class FeedbackFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FeedbackFileError';
  }
}

export interface FeedbackFilePayload {
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  fileData: string;
}

export function decodedBase64Bytes(fileData: string): number {
  const cleaned = fileData.replace(/\s/g, '');
  const padding = cleaned.endsWith('==') ? 2 : cleaned.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((cleaned.length * 3) / 4) - padding);
}

export function assertFeedbackAttachment(
  fileName: string,
  fileSizeBytes: number | null,
): void {
  if (isBlockedExecutable(fileName)) {
    throw new FeedbackFileError(getBlockedFileErrorMessage(fileName));
  }
  if (fileSizeBytes === null) {
    return;
  }
  if (!Number.isInteger(fileSizeBytes) || fileSizeBytes < 0) {
    throw new FeedbackFileError('That file could not be attached.');
  }
  if (fileSizeBytes > USER_FEEDBACK_MAX_FILE_BYTES) {
    throw new FeedbackFileError(FILE_TOO_LARGE_MESSAGE);
  }
}

export function feedbackFilePayload(input: {
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  fileData: string;
}): FeedbackFilePayload {
  const fileData = input.fileData.replace(/\s/g, '');
  if (fileData === '') {
    throw new FeedbackFileError('That file could not be attached.');
  }
  const fileName =
    input.fileName.trim() === '' ? 'attachment' : input.fileName.trim();
  assertFeedbackAttachment(fileName, input.fileSizeBytes);
  const mimeType = input.mimeType.trim();
  return {
    fileName,
    mimeType: mimeType === '' ? 'application/octet-stream' : mimeType,
    fileSizeBytes: input.fileSizeBytes,
    fileData,
  };
}
